/**
 * PageStatusBar — the status-bar + share-controls row for PageRenderer.
 *
 * Pure presentation. Holds the single-pill priority resolution (per
 * `feedback_status_single_line.md`) and the Edit/Download/Copy/Share/Preview/
 * Publish-Unpublish button row. Every data-testid, aria-label, title, and
 * className is preserved verbatim from PageRenderer — the publish-og tests
 * assert page-renderer-status-ready/error/publish/stale-og.
 */
import type { PagePublishState } from '../types';

interface PageStatusBarProps {
  publish: PagePublishState | undefined;
  publicUrl: string;
  draftHtmlHash: string | null;
  error: string | null;
  publishStatus: string | null;
  publishing: boolean;
  copied: boolean;
  inspectMode: boolean;
  canNativeShare: boolean;
  onToggleInspect: () => void;
  onDownload: () => void;
  onCopy: () => void;
  onNativeShare: () => void;
  onPreview: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
}

export default function PageStatusBar({
  publish,
  publicUrl,
  draftHtmlHash,
  error,
  publishStatus,
  publishing,
  copied,
  inspectMode,
  canNativeShare,
  onToggleInspect,
  onDownload,
  onCopy,
  onNativeShare,
  onPreview,
  onPublish,
  onUnpublish,
}: PageStatusBarProps) {
  // Single-pill resolution (per `feedback_status_single_line.md`):
  // exactly ONE label is shown at a time, picked by priority. We
  // never stack — the pill text is replaced in place. Priority:
  //   1. `error`              — hard failure, takes precedence.
  //   2. `publishStatus`      — active publish flow ("Preparing
  //      share image…", "Publishing…", "Published", soft-fail
  //      "Couldn't create share image — using generic preview").
  //   3. staleness            — passive notice; only shown when
  //      no active flow is running and the published OG was a
  //      `client-capture` whose hash no longer matches the draft.
  //   4. fallback             — "Ready".
  const publishedOg = publish?.ogImage;
  const ogIsClientCapture = publishedOg?.kind === 'client-capture';
  const ogHash = publishedOg?.contentHash;
  const isStale =
    !!publicUrl
    && ogIsClientCapture
    && typeof ogHash === 'string'
    && typeof draftHtmlHash === 'string'
    && ogHash !== draftHtmlHash;

  let pillText: string;
  let pillTestId: string;
  if (error) {
    pillText = error;
    pillTestId = 'page-renderer-status-error';
  } else if (publishStatus) {
    pillText = publishStatus;
    pillTestId = 'page-renderer-status-publish';
  } else if (isStale) {
    pillText = 'Share preview is from an earlier version. Republish to update.';
    pillTestId = 'page-renderer-status-stale-og';
  } else {
    pillText = 'Ready';
    pillTestId = 'page-renderer-status-ready';
  }

  return (
    <div className={`report-status-bar complete`}>
      <span className="status-dot" />
      <span data-testid={pillTestId}>{pillText}</span>
      <div className="report-share-controls">
        {/* Order:
              Pre-publish:  Edit → Download → Preview → Publish
              Post-publish: Edit → Download → Copy → Share → Preview → Unpublish
            Primary action (Publish or Unpublish) is always last.
            Naming: pre-publish trigger is "Publish"; the post-publish
            native share-sheet trigger is "Share". */}

        {/* Edit — PageRenderer only mounts for object-sourced pages
            (HtmlViewerModule handles legacy module pages), no guard.
            Active state flips to fully opaque accent background. */}
        <button
          type="button"
          className={`report-share-icon-btn${inspectMode ? ' report-share-icon-btn--active' : ''}`}
          onClick={onToggleInspect}
          aria-label="Toggle edit mode"
          aria-pressed={inspectMode}
          title="Edit"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
            <path d="M19.5 7.125L16.862 4.487" />
            <path d="M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
          </svg>
        </button>

        {/* Download — always available, even pre-publish. */}
        <button
          type="button"
          className="report-share-icon-btn"
          onClick={onDownload}
          aria-label="Download HTML"
          title="Download"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5" />
            <path d="M7.5 12L12 16.5m0 0L16.5 12M12 16.5V3" />
          </svg>
        </button>

        {/* Copy link — only after publish (no URL to copy beforehand). */}
        {publicUrl && (
          <button
            type="button"
            className="report-share-icon-btn"
            onClick={onCopy}
            title={copied ? 'Copied!' : 'Copy link'}
            aria-label={copied ? 'Copied!' : 'Copy link'}
          >
            {copied ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            )}
          </button>
        )}

        {/* Share — post-publish only, opens the native share sheet
            when the browser supports navigator.share. Lives between
            Copy and Preview so the post-publish bar reads
            Edit · Download · Copy · Share · Preview · Unpublish. */}
        {publicUrl && canNativeShare && (
          <button className="report-share-link-btn" onClick={onNativeShare} title="Open share sheet">
            Share
          </button>
        )}

        <button
          className="report-share-link-btn"
          onClick={onPreview}
          title="Fullscreen preview"
        >
          Preview
        </button>

        {/* Primary action — always the rightmost button.
              Pre-publish:  Publish (with inline spinner that turns
                            visible while publishing — width is
                            reserved either way so the button never
                            jumps).
              Post-publish: Unpublish (inverted-Publish styling —
                            same width / same border, just
                            transparent background + accent text). */}
        {publicUrl ? (
          <button className="report-unpublish-btn" onClick={onUnpublish} title="Unpublish">
            Unpublish
          </button>
        ) : (
          <button
            className="report-publish-btn"
            onClick={onPublish}
            disabled={publishing}
            aria-label={publishing ? 'Publishing…' : 'Publish'}
          >
            {publishing ? (
              /* Spinner REPLACES the label while a publish is in
                 flight. The button has a fixed `width` so the
                 toolbar layout doesn't shift when the inner content
                 swaps from "Publish" text to a 14px spinner. */
              <svg
                className="report-publish-spinner"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" opacity="0.25" />
                <path d="M12 2a10 10 0 0 1 10 10" />
              </svg>
            ) : (
              'Publish'
            )}
          </button>
        )}
      </div>
    </div>
  );
}
